import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router';
import { customerInfo as customerInfoShape } from 'constants/propTypes';
import { GET_LOYALTY_BOOKINGS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import Label from 'components/ui/Label';
import { BOOKINGS_SEARCH_TYPE } from 'constants/clubEurostar';
import BookingsTable from './BookingsTable';
import {
  Error,
  SectionContainer,
  SectionTitleContainer,
  BookingsTitle,
  BookingsTabsContainer,
  BookingsTabs,
  FindContainer,
  FindInput,
  ActionsList
} from '../ClubEurostarMember.styles';
import { StyledButton } from '../../HomePage/HomePage.styles';
import { Column, Row } from '../../../components/shared/Layout';
import { ActionButton } from '../../../components/ui/Button';
import { GET_BOOKING } from './graphql';
import { ADD_NOTE_TO_BOOKING, SAVE_BOOKING_TO_ALL_PROFILES } from '../../../store/mutations';
import AddBookingForm from './AddBookingForm';
import Modal from '../../../components/ui/Modal';
import { useManagedMutation } from '../../../utils/managedHooks';

const Home = ({ customerInfo, canPerformOperations, isLoungeStaffUser }) => {
  const tabs = [
    {
      value: 'upcoming-bookings',
      label: <FormattedMessage id="clubEurostar.upcoming" />
    },
    {
      value: 'travelled-bookings',
      label: <FormattedMessage id="clubEurostar.travelled" />
    }
  ];

  const { push } = useHistory();
  const client = useApolloClient();
  const [selectedTab, setSelectedTab] = useState(tabs[0].value);
  const [searchValue, setSearchValue] = useState('');
  const [isAddBookingPopupOpened, setAddBookingPopupOpened] = useState(false);
  const [addBookingError, setAddBookingError] = useState(null);
  const [getCustomerBookings, { loading, data, error }] = useLazyQuery(GET_LOYALTY_BOOKINGS, {
    errorPolicy: 'all'
  });
  const [addNote, { loading: addNoteLoading }] = useManagedMutation(ADD_NOTE_TO_BOOKING);
  const [saveBookingToAllProfiles, { loading: saveBookingToAllProfilesLoading }] = useMutation(SAVE_BOOKING_TO_ALL_PROFILES, {
    refetchQueries: ['profileBookings']
  });

  const isUpcoming = selectedTab === tabs[0].value;
  const hasSearchValue = !!searchValue;

  const membershipNumber = customerInfo.profile?.loyalty?.membershipNumber;
  const customerId = customerInfo.profile?.customerId;

  useEffect(() => {
    getCustomerBookings({
      variables: {
        customerId: customerInfo.profile?.customerId,
        lastName: customerInfo.lastName,
        page: 1,
        bookingSearchType: hasSearchValue
          ? BOOKINGS_SEARCH_TYPE.ALL
          : isUpcoming
          ? BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS
          : BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS,
        size: hasSearchValue ? 300 : 100
      }
    });
  }, [
    customerInfo.profile?.customerId,
    customerInfo.lastName,
    customerInfo.id,
    getCustomerBookings,
    isUpcoming,
    selectedTab,
    hasSearchValue
  ]);

  const bookings = data && data.profileBookings && data.profileBookings.items;

  const filteredBookings = useMemo(
    () =>
      hasSearchValue
        ? bookings?.filter((booking) => booking !== null && booking.reference?.toLowerCase().includes(searchValue?.toLowerCase()))
        : bookings?.filter((booking) => booking !== null),
    [bookings, searchValue, hasSearchValue]
  );

  const handleChange = (event) => {
    const value = event.target?.value || '';
    setSearchValue(value);
  };
  const onAddBookingClicked = () => {
    setAddBookingError(null);
    setAddBookingPopupOpened(true);
  };
  const onAddBookingSubmit = async (formData) => {
    if (formData.pnr) {
      try {
        const { errors } = await client.query({
          query: GET_BOOKING,
          variables: {
            reference: formData.pnr,
            lastName: formData.lastName
          }
        });
        if (errors && errors.length) {
          setAddBookingError(errors[0].message);
        }
        const result = await saveBookingToAllProfiles({
          variables: {
            customerId: customerId,
            bookingReference: formData.pnr
          }
        });
        if (result?.data?.saveBookingToAllProfiles.success) {
          await addNote({
            variables: {
              note: `Missing booking manually added to Club Eurostar account: ${customerInfo.profile.loyalty.membershipNumber}`,
              bookingReference: formData.pnr
            }
          });
          setAddBookingPopupOpened(false);
          setAddBookingError(null);
        } else {
          setAddBookingError(result?.data?.saveBookingToAllProfiles.success);
        }
      } catch (err) {
        setAddBookingError(err?.message || err);
      }
    }
  };

  return (
    <SectionContainer>
      <Modal
        isOpen={isAddBookingPopupOpened}
        title={<FormattedMessage id="clubEurostar.addBooking.modalTitle" />}
        subtitle={<FormattedMessage id={'clubEurostar.addBooking.modalSubtitle'} />}
        onClose={() => setAddBookingPopupOpened(false)}
      >
        <AddBookingForm
          error={addBookingError}
          onSubmit={onAddBookingSubmit}
          onCancel={() => setAddBookingPopupOpened(false)}
          loading={saveBookingToAllProfilesLoading || addNoteLoading}
        />
      </Modal>
      <ActionsList>
        <StyledButton
          onClick={() => push(`/search?membershipNumber=${membershipNumber}`)}
          disabled={!canPerformOperations}
          showTip={!canPerformOperations}
          textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
        >
          <FormattedMessage id="clubEurostar.bookWithoutSub" />
        </StyledButton>
        <ActionButton
          type="button"
          data-cy="add-booking-button"
          data-testid="add-booking-button"
          disabled={!canPerformOperations}
          showTip={!canPerformOperations}
          textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
          onClick={() => onAddBookingClicked()}
        >
          <FormattedMessage id="clubEurostar.addBooking" />
        </ActionButton>
      </ActionsList>
      <FindContainer>
        <Label htmlFor="findPNR">
          <FormattedMessage id="clubEurostar.findPNR" />
        </Label>
        <FindInput id="findPNR" value={searchValue} onChange={handleChange} data-cy="input-pnr" />
      </FindContainer>
      {!searchValue && (
        <SectionTitleContainer>
          <BookingsTitle>
            <FormattedMessage id="clubEurostar.trips" tagName="h3" />
          </BookingsTitle>
          <BookingsTabsContainer>
            <BookingsTabs useHash={false} onClick={setSelectedTab} selectedValue={selectedTab} tabs={tabs} />
          </BookingsTabsContainer>
        </SectionTitleContainer>
      )}
      {loading && (
        <Row span={16} height="10vh" data-testid="full-page-spinner">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      )}
      {!loading && (
        <>
          {error && (
            <Error>
              <FormattedMessage id="clubEurostar.defaultError" />
            </Error>
          )}
          {filteredBookings && !filteredBookings.length && (
            <Error>
              {!searchValue ? (
                <FormattedMessage id={`clubEurostar.${isUpcoming ? 'upcoming' : 'travelled'}BookingsNotFound`} />
              ) : (
                <FormattedMessage id="clubEurostar.notFoundForPNR" />
              )}
            </Error>
          )}

          {filteredBookings && filteredBookings.length > 0 && <BookingsTable bookings={filteredBookings} customerInfo={customerInfo} />}
        </>
      )}
    </SectionContainer>
  );
};

Home.propTypes = {
  customerInfo: customerInfoShape,
  canPerformOperations: PropTypes.bool,
  isLoungeStaffUser: PropTypes.bool
};

export default Home;
